import React from "react";
import { EASE_OUT } from "../../components/motion/useModalMotion";

// Motion for a creative card's life cycle. One authored moment, "going live": while the
// audio is processed the waveform is a pale ghost with a slow band of light travelling
// across it; when it goes live the bars rise into full magenta, left to right, and the
// card's edge turns magenta for the moment. No glow and no shadow: the bars carry it. A card
// created while the screen is open arrives with a short rise as the modal leaves.
// Everything else is quiet state feedback. Reduced motion keeps the color and label changes
// and drops the movement.


export const CREATIVE_CARD_CSS = `
  @keyframes creative-wave {
    0%, 100% { transform: scaleY(0.4); }
    50% { transform: scaleY(1); }
  }
  @keyframes creative-processing-sweep {
    0%, 100% { opacity: 0.14; transform: scaleY(0.55); }
    50% { opacity: 0.6; transform: scaleY(1); }
  }
  @keyframes creative-live-rise {
    from { opacity: 0.14; transform: scaleY(0.25); }
    to { opacity: 0.7; transform: scaleY(1); }
  }
  @keyframes creative-dot-breathe {
    0%, 100% { opacity: 0.35; transform: scale(0.75); }
    50% { opacity: 1; transform: scale(1); }
  }
  @keyframes creative-badge-in {
    from { opacity: 0; transform: translateY(3px) scale(0.92); }
    to { opacity: 1; transform: none; }
  }
  @keyframes creative-fade-in {
    from { opacity: 0.14; }
    to { opacity: 1; }
  }
  .creative-bar--playing { animation-name: creative-wave; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
  .creative-bar--processing { animation: creative-processing-sweep 1.8s cubic-bezier(0.45, 0, 0.55, 1) infinite both; }
  .creative-bar--rise { animation: creative-live-rise 560ms ${EASE_OUT} both; }
  .creative-card--live { border-color: #F90091 !important; }
  @keyframes creative-card-arrive { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  .creative-card--arrive { animation: creative-card-arrive 280ms ${EASE_OUT} both; }
  .creative-dot--processing { animation: creative-dot-breathe 1.8s ease-in-out infinite; }
  .creative-badge--enter { animation: creative-badge-in 320ms ${EASE_OUT} both; }
  @media (prefers-reduced-motion: reduce) {
    .creative-bar--playing { animation: none; }
    .creative-bar--processing { animation: none; opacity: 0.32; }
    .creative-bar--rise { animation: creative-fade-in 400ms ease-out both; }
    .creative-dot--processing { animation: none; }
    .creative-card--arrive { animation: creative-fade-in 200ms ease-out both; }
    .creative-badge--enter { animation: creative-fade-in 240ms ease-out both; }
  }
`;

const BAR_COUNT = 44;
// The light band takes this long to cross the waveform; the rise is capped so a wide card
// is still live within the moment.
const SWEEP_STEP_MS = 42;
const RISE_STEP_MS = 7;

const barHeight = (i) => `${14 + Math.sin(i * 0.7) * 20 + (i % 3) * 4}px`;

export function CreativeWaveform({ phase, isPlaying, justLive }) {
  const processing = phase === "processing";
  const color = phase === "inactive" || phase === "failed" ? "#E2E2E2" : "#F90091";
  return (
    <div
      aria-hidden="true"
      style={{
        background: processing ? "#FFF5FB" : "#FAFAFA",
        borderRadius: 10, height: 60, marginBottom: 14, display: "flex", alignItems: "center",
        padding: "0 14px", gap: 2, opacity: phase === "inactive" ? 0.4 : 1,
        transition: `background 400ms ${EASE_OUT}`,
      }}
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => {
        // Every animation goes through a class (only its timing is inline), so the
        // reduced-motion rules can stop it: an inline `animation` would beat the media query.
        const className = processing ? "creative-bar--processing" : justLive ? "creative-bar--rise" : isPlaying ? "creative-bar--playing" : undefined;
        const delay = processing ? i * SWEEP_STEP_MS : justLive ? i * RISE_STEP_MS : 0;
        return (
          <div
            key={i}
            className={className}
            style={{
              flex: 1,
              height: barHeight(i),
              background: color,
              borderRadius: 2,
              opacity: 0.7,
              transformOrigin: "center bottom",
              animationDuration: className === "creative-bar--playing" ? `${(0.3 + (i % 5) * 0.08).toFixed(2)}s` : undefined,
              animationDelay: className && className !== "creative-bar--playing" ? `${delay}ms` : undefined,
            }}
          />
        );
      })}
    </div>
  );
}

// Sentence case at 11px: the app floor for pills (taste-skill app rules; tiny tracked caps read
// as costume and fail legibility).
const BADGE_BASE = {
  display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, lineHeight: "16px", padding: "1px 8px",
  borderRadius: 20, fontWeight: 600, whiteSpace: "nowrap", flex: "none",
};

// `tag`/`tagColor` are the creative's own status from creative_statuses, shown as before for
// ready audio. Processing and failed are the card's own words, since the status still says
// Inactive for both.
export function CreativePhaseBadge({ phase, tag, tagColor, justLive }) {
  if (phase === "processing") {
    return (
      <span style={{ ...BADGE_BASE, background: "#FFF5FB", color: "#151515", boxShadow: "inset 0 0 0 1px #FFE9F6" }}>
        <span
          className="creative-dot--processing"
          style={{ width: 6, height: 6, borderRadius: "50%", background: "#F90091", flexShrink: 0 }}
        />
        Processing
      </span>
    );
  }
  if (phase === "failed") {
    return <span style={{ ...BADGE_BASE, background: "#FFF5F5", color: "#C62828" }}>Failed</span>;
  }
  const active = phase === "active";
  return (
    <span
      className={justLive ? "creative-badge--enter" : undefined}
      style={{ ...BADGE_BASE, background: active ? "#FFE9F6" : "#F5F5F5", color: active ? "#151515" : tagColor }}
    >
      {tag}
    </span>
  );
}

const HINTS = {
  processing: { text: "Preparing your audio. It goes live on its own in a few minutes.", color: "#626262" },
  // Grey: the Failed badge is the card's one red mark.
  failed: { text: "We couldn't process this audio. Create a new creative to try again.", color: "#626262" },
};

export function CreativePhaseHint({ phase }) {
  const hint = HINTS[phase];
  if (!hint) return null;
  return <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.45, color: hint.color }}>{hint.text}</div>;
}

// Visually hidden, still read by screen readers.
export const VISUALLY_HIDDEN = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden",
  clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0,
};
